import React from 'react';
import Box from '@mui/material/Box';
import IconButton, { IconButtonProps } from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import MoreVertOutlined from '@mui/icons-material/MoreVertOutlined';
import { shouldHideForbiddenActions } from '../../../utils/permission-ui';

export interface RowActionProps {
  /** Qué hace la acción; es el texto del tooltip y la etiqueta accesible. */
  label: string;
  icon: React.ReactNode;
  onClick?: () => void;
  /** Permiso de la acción. Sin permiso se oculta o se desactiva, como `PermissionButton`. */
  allowed?: boolean;
  /** Texto cuando no hay permiso. */
  deniedLabel?: string;
  disabled?: boolean;
  /** Texto del tooltip cuando está desactivada por otra razón (por ejemplo, solo se elimina el mismo día). */
  disabledLabel?: string;
  color?: IconButtonProps['color'];
  /** Enlace externo (por ejemplo, la ubicación en el mapa). */
  href?: string;
  /** Marca para los recorridos guiados (`data-tour`). */
  dataTour?: string;
}

/**
 * Acción por fila de las tablas de Préstamos: ícono con su descripción al pasar el mouse.
 * Respeta el permiso igual que `PermissionButton` (se oculta o queda desactivada).
 */
const RowAction: React.FC<RowActionProps> = ({
  label,
  icon,
  onClick,
  allowed = true,
  deniedLabel,
  disabled = false,
  disabledLabel,
  color = 'default',
  href,
  dataTour,
}) => {
  if (!allowed && shouldHideForbiddenActions()) return null;
  const isDisabled = disabled || !allowed;
  const title = !allowed ? deniedLabel ?? label : disabled && disabledLabel ? disabledLabel : label;
  const linkProps = href ? { component: 'a' as const, href, target: '_blank', rel: 'noopener noreferrer' } : {};
  return (
    <Tooltip title={title}>
      <Box component="span" sx={{ display: 'inline-flex' }} data-tour={dataTour}>
        <IconButton
          size="small"
          color={color}
          aria-label={label}
          disabled={isDisabled}
          onClick={isDisabled ? undefined : onClick}
          {...linkProps}
        >
          {icon}
        </IconButton>
      </Box>
    </Tooltip>
  );
};

export default RowAction;

export interface RowActionItem extends RowActionProps {
  key: string;
}

/**
 * Varias acciones por fila. Desde tableta se ven todas como íconos; en teléfono se ven las
 * `pinned` (por ejemplo, Registrar abono) y el resto pasa a un menú «Más acciones», para que la
 * columna de acciones no ocupe toda la pantalla.
 */
export const RowActionGroup: React.FC<{ items: RowActionItem[]; pinned?: string[]; moreLabel: string }> = ({
  items,
  pinned = [],
  moreLabel,
}) => {
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('md'));
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const hide = shouldHideForbiddenActions();
  const visible = items.filter((item) => item.allowed !== false || !hide);
  if (!compact) {
    return (
      <>
        {visible.map(({ key, ...item }) => (
          <RowAction key={key} {...item} />
        ))}
      </>
    );
  }
  const shown = visible.filter((item) => pinned.includes(item.key));
  const rest = visible.filter((item) => !pinned.includes(item.key));
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
      {shown.map(({ key, ...item }) => (
        <RowAction key={key} {...item} />
      ))}
      {rest.length > 0 ? (
        <>
          <Tooltip title={moreLabel}>
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <IconButton size="small" aria-label={moreLabel} aria-haspopup="menu" onClick={(e) => setAnchor(e.currentTarget)}>
                <MoreVertOutlined fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
          <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
            {rest.map((item) => {
              const disabled = item.disabled || item.allowed === false;
              const linkProps = item.href ? { component: 'a' as const, href: item.href, target: '_blank', rel: 'noopener noreferrer' } : {};
              return (
                <MenuItem
                  key={item.key}
                  disabled={disabled}
                  onClick={() => {
                    setAnchor(null);
                    if (!item.href) item.onClick?.();
                  }}
                  {...linkProps}
                >
                  <ListItemIcon sx={{ color: item.color === 'error' ? 'error.main' : undefined }}>{item.icon}</ListItemIcon>
                  <ListItemText>{item.allowed === false ? item.deniedLabel ?? item.label : item.label}</ListItemText>
                </MenuItem>
              );
            })}
          </Menu>
        </>
      ) : null}
    </Box>
  );
};
